import { useState } from 'react';
import DatePicker from 'react-datepicker';
import { fr } from 'date-fns/locale';
import { PlusCircle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

import { formatFilterDate } from './DataTableDateFilter';

import 'react-datepicker/dist/react-datepicker.css';

type DateRangeValue = {
    gte?: number;
    lte?: number;
};

type DataTableDateRangeFilterProps = {
    title: string;
    value: DateRangeValue | undefined;
    onChange: (value: DateRangeValue | undefined) => void;
};

const PRESETS = [
    {
        label: "Aujourd'hui",
        getDates: () => {
            const d = new Date();
            d.setHours(0, 0, 0, 0);
            return { gte: d.getTime(), lte: d.getTime() };
        },
    },
    {
        label: '7 derniers jours',
        getDates: () => {
            const end = new Date();
            const start = new Date();
            start.setDate(start.getDate() - 7);
            start.setHours(0, 0, 0, 0);
            return { gte: start.getTime(), lte: end.getTime() };
        },
    },
    {
        label: '30 derniers jours',
        getDates: () => {
            const end = new Date();
            const start = new Date();
            start.setDate(start.getDate() - 30);
            start.setHours(0, 0, 0, 0);
            return { gte: start.getTime(), lte: end.getTime() };
        },
    },
    {
        label: 'Ce mois',
        getDates: () => {
            const now = new Date();
            const start = new Date(now.getFullYear(), now.getMonth(), 1);
            return { gte: start.getTime(), lte: now.getTime() };
        },
    },
    {
        label: 'Mois dernier',
        getDates: () => {
            const now = new Date();
            const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            const end = new Date(now.getFullYear(), now.getMonth(), 0);
            return { gte: start.getTime(), lte: end.getTime() };
        },
    },
];

function formatBadge(value: DateRangeValue): string {
    if (value.gte && value.lte) {
        return `${formatFilterDate(value.gte)} → ${formatFilterDate(value.lte)}`;
    }
    if (value.gte) return `≥ ${formatFilterDate(value.gte)}`;
    if (value.lte) return `≤ ${formatFilterDate(value.lte)}`;
    return '';
}

export function DataTableDateRangeFilter({ title, value, onChange }: DataTableDateRangeFilterProps) {
    const [isOpen, setIsOpen] = useState(false);
    const isActive = value && (value.gte || value.lte);

    const startDate = value?.gte ? new Date(value.gte) : null;
    const endDate = value?.lte ? new Date(value.lte) : null;

    const handleRangeChange = (dates: [Date | null, Date | null]) => {
        const [start, end] = dates;
        if (!start && !end) {
            onChange(undefined);
            return;
        }
        const newValue: DateRangeValue = {};
        if (start) newValue.gte = start.getTime();
        if (end) {
            newValue.lte = end.getTime();
            setIsOpen(false);
        }
        onChange(Object.keys(newValue).length > 0 ? newValue : undefined);
    };

    const handlePreset = (preset: (typeof PRESETS)[number]) => {
        onChange(preset.getDates());
        setIsOpen(false);
    };

    return (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    size="sm"
                    className={cn('h-8 border-dashed bg-filter', isActive && 'bg-filter-active')}
                >
                    <PlusCircle className="size-3.5" />
                    {title}
                    {isActive && value && (
                        <>
                            <Separator orientation="vertical" className="mx-1 h-4" />
                            <Badge variant="secondary" className="rounded-sm px-1 font-normal text-xs">
                                {formatBadge(value)}
                            </Badge>
                        </>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
                <div className="flex">
                    <div className="flex flex-col gap-1 border-r p-3">
                        <div className="mb-1 text-xs font-medium text-muted-foreground">Périodes</div>
                        {PRESETS.map((preset) => (
                            <Button
                                key={preset.label}
                                variant="ghost"
                                size="sm"
                                className="justify-start text-xs h-7"
                                onClick={() => handlePreset(preset)}
                            >
                                {preset.label}
                            </Button>
                        ))}
                    </div>
                    <div className="p-3">
                        <DatePicker
                            selected={startDate}
                            onChange={handleRangeChange}
                            startDate={startDate}
                            endDate={endDate}
                            selectsRange
                            inline
                            locale={fr}
                            dateFormat="dd/MM/yyyy"
                        />
                    </div>
                </div>
                {isActive && (
                    <div className="border-t p-2">
                        <Button
                            variant="ghost"
                            size="sm"
                            className="w-full text-xs"
                            onClick={() => {
                                onChange(undefined);
                                setIsOpen(false);
                            }}
                        >
                            Effacer
                        </Button>
                    </div>
                )}
            </PopoverContent>
        </Popover>
    );
}
